import {lazy} from 'react'
import {Navigate} from 'react-router-dom'

const LandingPage = lazy(async () => await import('pages/LandingPage'))
const HowItWorks = lazy(async () => await import('pages/HowItWorks'))
const Register = lazy(async () => await import('pages/Register'))
const Login = lazy(async () => await import('pages/Login'))
const VerifyEmail = lazy(async () => await import('pages/VerifyEmail'))
const ForgotPassword = lazy(async () => await import('pages/ForgotPassword'))
const ResetPassword = lazy(async () => await import('pages/ResetPassword'))
const OnboardingWizard = lazy(
  async () => await import('components/OnboardingWizard')
)
const Profile = lazy(async () => await import('pages/Profile'))
const ProfilePublic = lazy(async () => await import('pages/ProfilePublic'))
const BusinessProfile = lazy(async () => await import('pages/BusinessProfile'))
const Home = lazy(async () => await import('pages/Home'))
const Discover = lazy(async () => await import('pages/Discover'))
const Messages = lazy(async () => await import('pages/Messages'))
const JoinCircle = lazy(async () => await import('pages/JoinCircle'))
const PostPage = lazy(async () => await import('pages/PostPage'))
const TagPage = lazy(async () => await import('pages/TagPage'))
const InviteFriendsPage = lazy(
  async () => await import('pages/InviteFriendsPage')
)
/*const ProfessionalsPage = lazy(
  async () => await import('pages/ProfessionalsPage')
)
const Jobs = lazy(async () => await import('pages/Jobs'))
const BrowseJobsPage = lazy(async () => await import('pages/BrowseJobsPage'))
const Classes = lazy(async () => await import('pages/Classes'))
const PublicJobPost = lazy(async () => await import('pages/PublicJobPost'))
const PublicClassPost = lazy(async () => await import('pages/PublicClassPost'))*/
const Credits = lazy(async () => await import('pages/Credits'))
const Connections = lazy(async () => await import('pages/Connections'))
const AddCredits = lazy(async () => await import('pages/AddCredits'))
const Checkout = lazy(async () => await import('pages/Checkout'))
const PremiumActivated = lazy(
  async () => await import('pages/PremiumActivated')
)
const SettingsPage = lazy(async () => await import('pages/Settings'))
const Assessment = lazy(async () => await import('pages/Assessment'))
const NotFound = lazy(async () => await import('pages/NotFound'))
const PrivacyPolicy = lazy(async () => await import('pages/PrivacyPolicy'))
const Contact = lazy(async () => await import('pages/Contact'))
const TermsOfService = lazy(async () => await import('pages/TermsOfService'))
// The unified search surface, plus the five entity-scoped views of it.
// Each page component is a thin wrapper that pins one entity; the lists
// themselves are pulled in by the surface's entity registry.
const SearchPage = lazy(async () => await import('pages/SearchPage'))
const BrowseProfessionalsPage = lazy(
  async () => await import('components/Browse/pages/ProfessionalsPage')
)
const BrowseStudiosPage = lazy(
  async () => await import('components/Browse/pages/StudiosPage')
)
const BrowseJobsPage = lazy(
  async () => await import('components/Browse/pages/JobsPage')
)
const BrowseClassesPage = lazy(
  async () => await import('components/Browse/pages/ClassesPage')
)
const BrowseSpacesPage = lazy(
  async () => await import('components/Browse/pages/SpacesPage')
)
const MapPage = lazy(async () => await import('pages/MapPage'))
const ConsumersLayout = lazy(
  async () => await import('components/ConsumersLayout')
)
const ConsumersList = lazy(async () => await import('components/ConsumersList'))
const JobPage = lazy(async () => await import('pages/JobPage'))
const EditJobPage = lazy(async () => await import('pages/EditJobPage'))
const CreateJobPage = lazy(async () => await import('pages/CreateJobPage'))
const ClassPage = lazy(async () => await import('pages/ClassPage'))
const EditClassPage = lazy(async () => await import('pages/EditClassPage'))
const CreateClassPage = lazy(async () => await import('pages/CreateClassPage'))
const SpacePage = lazy(async () => await import('pages/SpacePage'))
const EditSpacePage = lazy(async () => await import('pages/EditSpacePage'))
const BrowseJobsPageNew = lazy(
  async () => await import('pages/BrowseJobsPageNew')
)
const AdminLayout = lazy(async () => await import('pages/Admin/AdminLayout'))
const AdminDashboard = lazy(async () => await import('pages/Admin/Dashboard'))
const AdminAnalytics = lazy(async () => await import('pages/Admin/Analytics'))
const AdminBusinesses = lazy(async () => await import('pages/Admin/Businesses'))
const AdminSponsoredPostsPricing = lazy(
  async () => await import('pages/Admin/SponsoredPostsPricing')
)
const AdminImportSpaces = lazy(
  async () => await import('pages/Admin/ImportSpaces')
)
const AdminPosts = lazy(async () => await import('pages/Admin/Posts'))
const AdminOutreach = lazy(async () => await import('pages/Admin/Outreach'))
const AdminUsers = lazy(async () => await import('pages/Admin/Users'))
const AdminRewards = lazy(async () => await import('pages/Admin/Rewards'))
const AdminRewardsLedger = lazy(
  async () => await import('pages/Admin/RewardsLedger')
)
const AdminRewardsCatalog = lazy(
  async () => await import('pages/Admin/RewardsCatalog')
)
const AdminRewardsFulfillment = lazy(
  async () => await import('pages/Admin/RewardsFulfillment')
)
const AdminEmailTemplates = lazy(
  async () => await import('pages/Admin/EmailTemplates')
)
const AdminEmailTemplateEditor = lazy(
  async () => await import('pages/Admin/EmailTemplateEditor')
)
const AdminScheduledEmails = lazy(
  async () => await import('pages/Admin/ScheduledEmails')
)
const AdminScheduledEmailCreate = lazy(
  async () => await import('pages/Admin/ScheduledEmailCreate')
)
const AdminBots = lazy(async () => await import('pages/Admin/Bots'))
const AdminPostsRedirect = () => <Navigate to="/admin/feed/posts" replace />
// The old role picker lived here; role is no longer chosen after sign-in.
const AuthCallbackRedirect = () => <Navigate to="/homepage" replace />
const AdminFeedPlacements = lazy(
  async () => await import('pages/Admin/FeedPlacements')
)
const AdminAnnouncements = lazy(
  async () => await import('pages/Admin/Announcements')
)
const AdminSharpets = lazy(async () => await import('pages/Admin/Sharpets'))
const ClaimPage = lazy(async () => await import('pages/ClaimPage'))

const routes = [
  {
    path: '/',
    name: 'LandingPage',
    exact: true,
    component: LandingPage,
    protected: false,
  },
  {
    // Public walkthrough video, linked from the home page footer.
    path: '/how-it-works',
    name: 'HowItWorks',
    exact: true,
    component: HowItWorks,
    protected: false,
  },
  {
    path: '/register',
    name: 'Register',
    exact: true,
    component: Register,
    protected: false,
  },
  {
    path: '/login',
    name: 'Login',
    exact: false,
    component: Login,
    protected: false,
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    exact: true,
    component: VerifyEmail,
    protected: false,
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    exact: true,
    component: ForgotPassword,
    protected: false,
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    exact: false,
    component: ResetPassword,
    protected: false,
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    exact: false,
    component: OnboardingWizard,
    protected: true,
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    exact: true,
    component: AuthCallbackRedirect,
    protected: true,
  },
  {
    path: '/homepage',
    name: 'Home',
    exact: true,
    component: Home,
    protected: true,
  },
  {
    path: '/discover',
    name: 'Discover',
    exact: true,
    component: Discover,
    protected: true,
  },
  {
    // Circles + DMs. The optional param drives mobile drill-in: /messages
    // is the list, /messages/:conversationId the thread.
    path: '/messages/:conversationId?',
    name: 'Messages',
    exact: true,
    component: Messages,
    protected: true,
  },
  {
    // Public Circle-invite landing page (behind the demo QR codes). No
    // login wall — signup happens after the visitor sees the group card.
    path: '/join/:token',
    name: 'JoinCircle',
    exact: true,
    component: JoinCircle,
    protected: false,
  },
  {
    // Permalink for a single feed post + its comment thread. Feed cards
    // link here (body text, timestamp, "View all N comments").
    path: '/post/:postId',
    name: 'Post',
    exact: true,
    component: PostPage,
    // Public: the permalink is a home-page card destination; getPost is token-optional and actions gate themselves.
    protected: false,
  },
  {
    // Every post carrying a hashtag, newest first. Feed hashtags link
    // here; competitions collect their entries this way.
    path: '/tag/:tag',
    name: 'Tag',
    exact: true,
    component: TagPage,
    protected: true,
  },
  {
    // Referral invites — the person+plus icon in the header lands here.
    path: '/invite-friends',
    name: 'InviteFriends',
    exact: true,
    component: InviteFriendsPage,
    protected: true,
  },
  {
    // The unified search surface. /professionals, /businesses, /jobs,
    // /classes and /spaces below are the same component with one entity
    // preselected — they no longer host their own list via an Outlet.
    path: '/search',
    name: 'Search',
    exact: true,
    component: SearchPage,
    protected: true,
  },
  {
    path: '/professionals',
    name: 'Professionals',
    exact: true,
    component: BrowseProfessionalsPage,
    protected: true,
  },
  {
    path: '/businesses',
    name: 'Businesses',
    exact: true,
    component: BrowseStudiosPage,
    protected: true,
  },
  {
    path: '/consumers',
    name: 'Consumers',
    exact: false,
    component: ConsumersLayout,
    protected: true,
    children: [
      {
        path: '',
        name: 'Consumers List',
        component: ConsumersList,
        index: true,
      },
    ],
  },
  {
    path: '/settings/:section?',
    name: 'Settings',
    exact: false,
    component: SettingsPage,
    protected: true,
  },
  {
    path: '/profile',
    name: 'Profile',
    exact: true,
    component: Profile,
    protected: true,
  },
  {
    // "My network": your connections and requests (?tab=requests), or
    // someone else's connections (?user=<id>).
    path: '/connections',
    name: 'Connections',
    exact: true,
    component: Connections,
    protected: true,
  },
  {
    path: '/profile/:id',
    name: 'Profile Public',
    exact: false,
    component: ProfilePublic,
    protected: false,
  },
  {
    path: '/business/:businessId',
    name: 'Business Public',
    exact: false,
    component: BusinessProfile,
    protected: false,
  },
  {
    path: '/create-job-post/:jobId',
    name: 'CreateJobPost',
    exact: false,
    component: Profile,
    protected: true,
  },
  {
    path: '/create-shared-job-post/:jobId',
    name: 'CreateSharedJobPost',
    exact: false,
    component: Profile,
    protected: true,
  },
  {
    path: '/edit-job-post/:jobId',
    name: 'EditJobPost',
    exact: false,
    component: Profile,
    protected: true,
  },
  {
    path: '/edit-shared-job-post/:jobId',
    name: 'EditSharedJobPost',
    exact: false,
    component: Profile,
    protected: true,
  },
  {
    path: '/jobs',
    name: 'Jobs',
    exact: true,
    component: BrowseJobsPage,
    protected: true,
  },
  {
    path: '/classes',
    name: 'Classes',
    exact: true,
    component: BrowseClassesPage,
    // Public: the classes list API is token-optional, like /spaces and /browse-jobs.
    protected: false,
  },
  {
    path: '/spaces',
    name: 'Spaces',
    exact: true,
    component: BrowseSpacesPage,
    // Public: browse spaces without an account (discovery + shareable).
    // The spaces list has no auth-only card actions; the read API
    // (searchSpaces) is token-optional.
    protected: false,
  },
  {
    // /map replaces /spaces in the top nav (per the 2026/05/12 product
    // call). The /spaces route stays mounted above so existing links
    // and the Browse list page keep working — they're just no longer
    // discoverable from the chrome.
    path: '/map',
    name: 'Map',
    exact: false,
    component: MapPage,
    // Public: the Zillow-style discovery map is the primary logged-out
    // landing surface. Read data comes from the public /map endpoint;
    // auth-only affordances (pin/favorite, Post) gate themselves.
    protected: false,
  },
  {
    // Sibling of `/spaces` so the detail view can render its own layout
    // outside the BrowseLayout chrome. React Router v6 prefers the more
    // specific path for `/spaces/:spaceId`.
    //
    // Public (protected: false) so a space link can be shared off-platform
    // (e.g. an admin DMs a salon owner the listing on Facebook) and viewed
    // without an account. Auth-only actions on the page (Message) gate
    // themselves; the read API (getSpace) is already token-optional.
    path: '/spaces/:spaceId',
    name: 'SpacePage',
    exact: false,
    component: SpacePage,
    protected: false,
  },
  {
    path: '/job/:jobId',
    name: 'JobPage',
    exact: false,
    component: JobPage,
    // Public: logged-out visitors get the read-only view (JobPage → PublicJobPost); apply goes through login.
    protected: false,
  },
  {
    path: '/edit-job/:jobId',
    name: 'EditJobPage',
    exact: false,
    component: EditJobPage,
    protected: true,
  },
  {
    path: '/create-job',
    name: 'CreateJobPage',
    exact: false,
    component: CreateJobPage,
    protected: true,
  },
  {
    path: '/edit-shared-job/:jobId',
    name: 'EditSharedJobPage',
    exact: false,
    component: EditJobPage,
    protected: true,
  },
  {
    path: '/create-shared-job',
    name: 'CreateSharedJobPage',
    exact: false,
    component: CreateJobPage,
    protected: true,
  },
  {
    path: '/class/:classId',
    name: 'ClassPage',
    exact: false,
    component: ClassPage,
    // Public: logged-out visitors get the read-only view (ClassPage → PublicClassPost); sign-up goes through login.
    protected: false,
  },
  {
    path: '/edit-class/:classId',
    name: 'EditClassPage',
    exact: false,
    component: EditClassPage,
    protected: true,
  },
  {
    path: '/edit-space/:spaceId',
    name: 'EditSpacePage',
    exact: false,
    component: EditSpacePage,
    protected: true,
  },
  {
    path: '/create-class',
    name: 'CreateClassPage',
    exact: false,
    component: CreateClassPage,
    protected: true,
  },
  {
    path: '/assessment',
    name: 'Assessment',
    exact: false,
    component: Assessment,
    protected: true,
  },
  {
    path: '/browse-jobs',
    name: 'BrowseJobs',
    exact: false,
    component: BrowseJobsPageNew,
    protected: false,
  },
  {
    path: '/profiles',
    name: 'Profiles',
    exact: false,
    component: Profile,
    protected: true,
  },
  {
    // Always registered (the header coin icon links here for every
    // role); purchase routes below stay premium-gated.
    path: '/credits',
    name: 'Credits',
    exact: true,
    component: Credits,
    protected: true,
  },
  process.env.REACT_APP_ENABLE_PREMIUM === 'true'
    ? {
        path: '/add-credits',
        name: 'AddCredits',
        exact: true,
        component: AddCredits,
        protected: true,
      }
    : {},
  process.env.REACT_APP_ENABLE_PREMIUM === 'true'
    ? {
        path: '/checkout',
        name: 'Checkout',
        exact: true,
        component: Checkout,
        protected: true,
      }
    : {},
  process.env.REACT_APP_ENABLE_PREMIUM === 'true'
    ? {
        path: '/premium-activated',
        name: 'PremiumActivated',
        exact: true,
        component: PremiumActivated,
        protected: true,
      }
    : {},
  {
    // /admin/* — gated by AdminRoute (role+isAdmin check). AdminLayout
    // renders its own <Outlet> for child routes. Child routes are flat
    // here (no further nesting) so the App.js renderer can spread them
    // into one <Route> with nested <Route> children.
    path: '/admin',
    name: 'Admin',
    exact: false,
    component: AdminLayout,
    admin: true,
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: AdminDashboard,
        index: true,
      },
      {path: 'analytics', name: 'AdminAnalytics', component: AdminAnalytics},
      {path: 'businesses', name: 'AdminBusinesses', component: AdminBusinesses},
      {
        path: 'sponsored-posts',
        name: 'AdminSponsoredPostsPricing',
        component: AdminSponsoredPostsPricing,
      },
      {
        path: 'import-spaces',
        name: 'AdminImportSpaces',
        component: AdminImportSpaces,
      },
      {path: 'users', name: 'AdminUsers', component: AdminUsers},
      {
        // Posts moderation moved under Feed (/admin/feed/:kind); keep old
        // links and bookmarks working.
        path: 'posts',
        name: 'AdminPostsRedirect',
        component: AdminPostsRedirect,
      },
      {path: 'outreach', name: 'AdminOutreach', component: AdminOutreach},
      {path: 'rewards', name: 'AdminRewards', component: AdminRewards},
      {
        path: 'rewards/catalog',
        name: 'AdminRewardsCatalog',
        component: AdminRewardsCatalog,
      },
      {
        path: 'rewards/fulfillment',
        name: 'AdminRewardsFulfillment',
        component: AdminRewardsFulfillment,
      },
      {
        path: 'rewards/activity',
        name: 'AdminRewardsLedger',
        component: AdminRewardsLedger,
      },
      {
        path: 'email-templates',
        name: 'AdminEmailTemplates',
        component: AdminEmailTemplates,
      },
      {
        path: 'email-templates/new',
        name: 'AdminEmailTemplateNew',
        component: AdminEmailTemplateEditor,
      },
      {
        path: 'email-templates/:id',
        name: 'AdminEmailTemplateEdit',
        component: AdminEmailTemplateEditor,
      },
      {
        path: 'scheduled-emails',
        name: 'AdminScheduledEmails',
        component: AdminScheduledEmails,
      },
      {
        path: 'scheduled-emails/new',
        name: 'AdminScheduledEmailCreate',
        component: AdminScheduledEmailCreate,
      },
      {
        // Draft editing — same page as create, loaded from the id.
        path: 'scheduled-emails/:id',
        name: 'AdminScheduledEmailEdit',
        component: AdminScheduledEmailCreate,
      },
      {path: 'bots', name: 'AdminBots', component: AdminBots},
      {
        // Feed > Admin tab: featured and sponsored posts slotted into the
        // feed. The default tab, so it owns the bare /admin/feed.
        path: 'feed',
        name: 'AdminFeedPlacements',
        component: AdminFeedPlacements,
      },
      {
        // Feed > Posts / Jobs / Spaces / Classes moderation tabs.
        path: 'feed/:kind',
        name: 'AdminPosts',
        component: AdminPosts,
      },
      {
        path: 'announcements',
        name: 'AdminAnnouncements',
        component: AdminAnnouncements,
      },
      {path: 'sharpets', name: 'AdminSharpets', component: AdminSharpets},
    ],
  },
  {
    // Public claim landing — reads ?token=<JWT> from URL. No auth.
    path: '/claim',
    name: 'Claim',
    exact: false,
    component: ClaimPage,
    protected: false,
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    exact: true,
    component: PrivacyPolicy,
    protected: false,
  },
  {
    // Public: linked from the logged-out footer as well as from Settings.
    path: '/contact',
    name: 'Contact',
    exact: true,
    component: Contact,
    protected: false,
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    exact: true,
    component: TermsOfService,
    protected: false,
  },
  {
    path: '*',
    name: 'NotFound',
    exact: false,
    component: NotFound,
    protected: false,
  },
]

export default routes
